// The testable portion of IdleInput.js. No browser or timer dependencies.

const EventEmitter = require("events");

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const IdleEngine = function(data) {
	if (!(this instanceof IdleEngine)) {
		return new IdleEngine(data);
	}
	this.getNow = data.getNow;
	this.resetTimeout = data.resetTimeout;

	this.emitter = new EventEmitter();
	// status is always "active", "idle", or "hidden".
	this.status = "active";
	// The length of time to wait before emitting an idle event.
	this.idleDuration = data.idleDuration || 60000;
	// The last time an input that can interrupt an idle period was received.
	this.lastInputTime = data.getNow();
	// Array of [start, end] sorted from most recent to least. start and end are
	// getNow() returns, and each [start, end] is an interval during which the
	// page was active. If status is active, the end value of the first interval
	// should be ignored.
	this.activeIntervals = [];

	startNewInterval(this, data.getNow());
};

// Private interval tracking helpers

const startNewInterval = function(eng, startTime) {
	eng.activeIntervals.unshift([startTime, startTime]);
	if (eng.activeIntervals.length > 10) {
		eng.activeIntervals = eng.activeIntervals.slice(0, 10);
	}
};

const updateLastIntervalEnd = function(eng, endTime) {
	if (eng.activeIntervals.length) {
		eng.activeIntervals[0][1] = endTime;
	}
};

// Gets the interval at the index, except the first active interval's end
// value is replaced by the expected idle timeout if we're currently active.
const getInterval = function(eng, index) {
	if (index === 0 && eng.status === "active") {
		return [eng.activeIntervals[0][0], eng.lastInputTime + eng.idleDuration];
	}
	return eng.activeIntervals[index];
};

// Status change triggers

IdleEngine.prototype.triggerBlur = function() {
	const now = Math.min(this.lastInputTime + this.idleDuration, this.getNow());
	if (this.status === "active") {
		updateLastIntervalEnd(this, now);
	}
	this.status = "hidden";
	this.emitter.emit("blur");
	this.emitter.emit("idle");
	this.emitter.emit("status", this.status);
};

IdleEngine.prototype.triggerFocus = function() {
	const now = this.getNow();
	if (this.status !== "active") {
		startNewInterval(this, now);
	}
	this.status = "active";
	this.emitter.emit("focus");
	this.emitter.emit("wake");
	this.emitter.emit("status", this.status);
};

IdleEngine.prototype.triggerIdle = function() {
	const now = Math.min(this.lastInputTime + this.idleDuration, this.getNow());
	if (this.status === "active") {
		updateLastIntervalEnd(this, now);
	}
	this.status = "idle";
	this.emitter.emit("idle");
	this.emitter.emit("status", this.status);
};

IdleEngine.prototype.triggerWake = function() {
	const now = this.getNow();
	if (this.status !== "active") {
		startNewInterval(this, now);
	}
	this.status = "active";
	this.emitter.emit("wake");
	this.emitter.emit("status", this.status);
};

// Internal only. Call when an idle timeout should have fired but we stayed
// active throughout.
const triggerSleep = function(eng, expectedIdleTime) {
	// Note this is only called if status is active, so we don't update status.
	const now = eng.getNow();
	updateLastIntervalEnd(eng, expectedIdleTime);
	startNewInterval(eng, now);
	eng.emitter.emit("sleep", eng.lastInputTime);
};

// Main interface

// Call to reset the idle timeout.
IdleEngine.prototype.handleInput = function(oldIdleDuration) {
	oldIdleDuration = oldIdleDuration || this.idleDuration;
	const now = this.getNow();
	if (this.status !== "active") {
		this.triggerWake();
	} else if (this.lastInputTime + oldIdleDuration < now) {
		triggerSleep(this, this.lastInputTime + oldIdleDuration);
	}
	this.lastInputTime = now;
	this.resetTimeout(() => {
		if (this.status === "active") {
			this.triggerIdle();
		}
	}, this.idleDuration);
};

// Get the duration of the idle timeout in milliseconds.
IdleEngine.prototype.getIdleDuration = function() {
	return this.idleDuration;
};

// Set the duration of the idle timeout in milliseconds. Automatically restarts
// the timer so the new value takes effect immediately.
IdleEngine.prototype.setIdleDuration = function(newValue) {
	const oldIdleDuration = this.idleDuration;
	this.idleDuration = newValue;
	this.handleInput(oldIdleDuration);
};

// Gets the total length of the intersection of the given interval with all
// active intervals.
IdleEngine.prototype.getActiveBetween = function(startTime, endTime) {
	if (!this.activeIntervals.length) {
		return 0;
	}
	let index = 0;
	// First, find the interval with endTime
	while (getInterval(this, index)[0] > endTime) {
		index += 1;
		if (index >= this.activeIntervals.length) {
			return 0;
		}
	}
	// Then, find the interval with startTime, while adding up total time in between.
	let totalTime = 0;
	let interval = getInterval(this, index);
	while (interval && interval[1] > startTime) {
		totalTime +=
			Math.min(interval[1], endTime) - Math.max(interval[0], startTime);
		index += 1;
		interval = getInterval(this, index);
	}
	return totalTime;
};

// Status change listeners.
IdleEngine.prototype.listenToBlur = function(cb) {
	this.emitter.on("blur", cb);
};
IdleEngine.prototype.listenToFocus = function(cb) {
	this.emitter.on("focus", cb);
};
IdleEngine.prototype.listenToIdle = function(cb) {
	this.emitter.on("idle", cb);
};
IdleEngine.prototype.listenToWake = function(cb) {
	this.emitter.on("wake", cb);
};
// Not a status change, but if we go active -> active without an internal
// wakeup for a full idle duration and somehow the timeout didn't fire, we fire
// a sleep instead. No arguments
IdleEngine.prototype.listenToSleep = function(cb) {
	this.emitter.on("sleep", cb);
};
// Callback called with a single argument, which is one of "active", "idle", "hidden".
IdleEngine.prototype.listenToStatus = function(cb) {
	this.emitter.on("status", cb);
};

module.exports = IdleEngine;
